/**
 * The page's entry. It starts the renderer, wires the page's parts to the
 * camera and the globe, and starts the loop. Without WebGL a 2D canvas still
 * takes the globe's place and the page reads as a plain document.
 */
import { createCamera } from './camera'
import { applyCitiesBin } from './data/cities'
import { debug } from './debug'
import { createFrameMeter } from './frame-meter'
import { createGlobe, BACK_A } from './globe'
import { DOLLY, LAM_END, R0, R1, SPIN, TILT } from './globe-look'
import { createIntro } from './intro/intro'
import { EXPAND_POW } from './intro/supernova'
import { createLoop, type Loop } from './loop'
import { createNetwork } from './network'
import { wireContactForm } from './page/contact-form'
import { revealEmails } from './page/email'
import { createCue } from './page/cue'
import { createOutline } from './page/outline'
import { createPins } from './page/pins'
import { createPlaceList } from './page/place-list'
import { readStops } from './page/stops'
import { wireSwitcher } from './page/switcher'
import { carriedSection, readMarker } from './page/visit'
import { createRenderer } from './renderer/renderer'
import { showStill } from './still-canvas'
import { isPhone, measureViewport, type Viewport } from './viewport'
import { CITIES_BIN_URL } from 'virtual:cities-bin-url'

/* =====================================================================
   TWEAKABLES
   ===================================================================== */
const MAX_DPR = 3 /* the canvas's sharpness cap, in device pixels per CSS pixel (phones are 3, laptops 2; lower = softer but less to draw) */
const RESIZE_DELAY = 140 /* ms the window must rest before the page measures itself again */
/* ===================================================================== */

function byId<T extends HTMLElement>(id: string): T {
  return document.getElementById(id) as T
}

async function boot() {
  ;(window as { __nxDisarm?: () => void }).__nxDisarm?.()
  /* A same-site arrival carries the section to open on in `?from=`; read and
     strip it before anything else runs, so the clean URL is in place at once. */
  const from = readMarker()
  const canvas = byId<HTMLCanvasElement>('c')
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  /* Fetch the binary cities asset (preloaded by <link rel=preload> in <head>).
     On failure the inline ENCODED fallback in cities.ts stays in effect. */
  await fetch(CITIES_BIN_URL)
    .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject()))
    .then((buf) => applyCitiesBin(buf))
    .catch(() => {})

  /* Everything is drawn with WebGL (see src/renderer): the heavy layers on the
     graphics card, and everything else through `renderer.ctx`, a stand-in for
     the 2D canvas, so code from nexial_home.html can be pasted in unchanged. */
  const renderer = createRenderer(canvas, {
    r0: R0,
    r1: R1,
    tilt: TILT,
    lamEnd: LAM_END,
    spin: SPIN,
    dolly: DOLLY,
    expandPow: EXPAND_POW,
    backA: BACK_A,
  })
  const intro = createIntro(renderer)

  /* the stops, read from the page, and the parts of the page that show them */
  const stops = readStops()
  const places = stops.filter((stop) => !stop.global)
  const pins = createPins(byId('pins'), places)
  const camera = createCamera({ stops, servicesEl: byId('services') })
  /* the loop is made last, as it needs all of these; until it is up (and for good without
     WebGL) there is no camera to fly, so a jump just scrolls the page */
  function scrollToSection(section: HTMLElement) {
    section.scrollIntoView({ block: section.classList.contains('stop') ? 'center' : 'start' })
  }
  let jump: Loop['goTo'] = scrollToSection
  function goTo(section: HTMLElement) {
    jump(section)
  }
  const outline = createOutline(
    byId('outline'),
    Array.from(document.querySelectorAll<HTMLElement>('[data-nav]')),
    goTo
  )
  const placeList = createPlaceList({
    el: byId('gnav'),
    stops,
    jump: (stop) => goTo(stop.el),
    /* pointing at a place in the list on the hero points at it in the outline too */
    point: (stop, on) => outline.peek(on ? stop.el : undefined),
  })
  wireSwitcher(byId('lang'))
  /* TODO(#95): the contact form is not rendered until it has an endpoint; wire it again then */
  const form = document.getElementById('form')
  if (form instanceof HTMLFormElement) wireContactForm(form)
  revealEmails()
  const cue = createCue(byId('cue'))

  if (!renderer.init()) {
    canvas.remove()
    const root = document.documentElement
    root.classList.remove('path-intro', 'path-content')
    root.classList.add('path-poster')
    const top = byId('top')
    for (const el of [top, byId('about'), ...stops.map((stop) => stop.el)]) el.classList.add('on')
    outline.show(true)
    function scrolled() {
      top.classList.toggle('stuck', window.pageYOffset > 40)
      outline.mark(measureViewport(1))
    }
    scrolled()
    window.addEventListener('scroll', scrolled, { passive: true })
    const repaint = showStill(document.getElementById('preloader'))
    let resizing: ReturnType<typeof setTimeout>
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizing)
        resizing = setTimeout(repaint, RESIZE_DELAY)
      },
      { passive: true }
    )
    return
  }
  const network = createNetwork(renderer.ctx)
  const globe = createGlobe({ ctx: renderer.ctx, renderer, places, network })

  function measure(): Viewport {
    const viewport = measureViewport(debug.dpr ?? MAX_DPR)
    canvas.width = Math.round(viewport.width * viewport.dpr)
    canvas.height = Math.round(viewport.height * viewport.dpr)
    canvas.style.width = viewport.width + 'px'
    canvas.style.height = viewport.height + 'px'
    return viewport
  }

  const loop = createLoop({
    renderer,
    intro,
    camera,
    globe,
    pins,
    outline,
    placeList,
    cue,
    stops,
    places,
    viewport: measure(),
    reduced,
    carried: carriedSection(from),
  })
  jump = loop.goTo
  intro.measureArms()
  intro.measureWordmark()
  intro.reseed(isPhone())
  loop.start()

  const meter = import.meta.env.DEV && debug.fps ? createFrameMeter() : null
  function tick(now: number) {
    requestAnimationFrame(tick)
    if (meter) meter.measure(now, () => loop.frame(now), loop.viewport)
    else loop.frame(now)
  }
  const root = document.documentElement
  requestAnimationFrame((now) => {
    tick(now)
    if (root.classList.contains('path-intro')) {
      document.getElementById('preloader')?.remove()
    }
    if (root.classList.contains('path-content')) {
      root.classList.add('canvas-settled')
    }
  })

  /* the intro measures the type again once the face is there */
  if (document.fonts && document.fonts.load) {
    document.fonts.load(intro.font, 'X').then(
      () => loop.fontsLoaded(),
      () => {}
    )
  }

  document.querySelector('.brand')!.addEventListener('click', (e) => {
    e.preventDefault()
    loop.goTo(byId('about'))
  })
  window.addEventListener(
    'pointermove',
    (e) => {
      if (e.pointerType !== 'touch') loop.pointerAt(e.clientX, e.clientY)
    },
    { passive: true }
  )
  let resizing: ReturnType<typeof setTimeout>
  window.addEventListener(
    'resize',
    () => {
      clearTimeout(resizing)
      resizing = setTimeout(() => loop.resized(measure()), RESIZE_DELAY)
    },
    { passive: true }
  )
}

boot()
